import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Eye, EyeSlash, User, Envelope, ArrowLeft } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import LustLinkSeal from '../components/common/LustLinkSeal';
import { toast } from 'sonner';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') === 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    display_name: '',
    role: searchParams.get('role') || 'viewer',
    gender: '',
    pronouns: '',
    referral_code: searchParams.get('ref') || sessionStorage.getItem('referral_code') || '',
  });

  useEffect(() => {
    setIsLogin(searchParams.get('mode') === 'login');
    // Capture referral code from URL and persist in session
    const ref = searchParams.get('ref');
    if (ref) {
      sessionStorage.setItem('referral_code', ref);
      setFormData(prev => ({ ...prev, referral_code: ref }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!formData.display_name) {
          toast.error('Display name is required');
          setLoading(false);
          return;
        }
        await register(formData);
        toast.success('Account created! Welcome to LustLink.');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1774532969543-d2872d588943?w=1200&q=80&auto=format"
          alt=""
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b]/80 via-transparent to-[#09090b]/40" />
        <div className="absolute inset-0 flex items-end p-12">
          <div className="max-w-md">
            <Link to="/" className="flex items-center gap-3 mb-8">
              <LustLinkSeal size={40} showArcText={false} />
              <span className="font-heading text-2xl font-bold text-white">LustLink</span>
            </Link>
            <h2 className="font-heading text-4xl font-bold text-white mb-4 leading-tight">
              {isLogin ? 'Welcome Back' : 'Your Desires,\nYour Rules'}
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              {isLogin 
                ? 'Sign in to access exclusive content and live experiences.'
                : 'Join thousands of creators and fans on the most empowering adult platform.'
              }
            </p>
          </div>
        </div>
      </div>
      
      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <Link to="/" className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-slate-400" />
              <LustLinkSeal size={40} showArcText={false} />
              <span className="font-heading text-2xl font-bold text-white">LustLink</span>
            </Link>
          </div>
          
          {/* Toggle */}
          <div className="flex rounded-full bg-slate-900 p-1 mb-8">
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 rounded-full font-medium transition-all ${
                !isLogin ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="register-tab"
            >
              Sign Up
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 rounded-full font-medium transition-all ${
                isLogin ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="login-tab"
            >
              Sign In
            </button>
          </div>
          
          {/* Form */}
          <AnimatePresence mode="wait">
            <motion.form
              key={isLogin ? 'login' : 'register'}
              initial={{ opacity: 0, x: isLogin ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isLogin ? 20 : -20 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* Display Name (Register only) */}
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Display Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="text"
                      name="display_name"
                      value={formData.display_name}
                      onChange={handleChange}
                      placeholder="How should we call you?"
                      className="w-full bg-slate-900 border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                      data-testid="display-name-input"
                    />
                  </div>
                </div>
              )}
              
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="w-full bg-slate-900 border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                    data-testid="password-input"
                  />
                </div>
              </div>
              
              {/* Role Selection (Register only) */}
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    I want to
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, role: 'viewer' })}
                      className={`p-4 rounded-xl border transition-all ${
                        formData.role === 'viewer'
                          ? 'border-blue-500 bg-blue-500/10 text-blue-500'
                          : 'border-white/10 text-slate-400 hover:border-white/20'
                      }`}
                      data-testid="viewer-role-btn"
                    >
                      <Eye className="w-6 h-6 mx-auto mb-2" />
                      <span className="block font-medium">Explore</span>
                      <span className="text-xs opacity-70">View content</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, role: 'creator' })}
                      className={`p-4 rounded-xl border transition-all ${
                        formData.role === 'creator'
                          ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                          : 'border-white/10 text-slate-400 hover:border-white/20'
                      }`}
                      data-testid="creator-role-btn"
                    >
                      <Heart className="w-6 h-6 mx-auto mb-2" />
                      <span className="block font-medium">Create</span>
                      <span className="text-xs opacity-70">Share content</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Gender & Preferences (Register only) */}
              {!isLogin && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      I am
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                      data-testid="gender-select"
                    >
                      <option value="">Select gender</option>
                      <option value="Man">Man</option>
                      <option value="Woman">Woman</option>
                      <option value="Trans Man">Trans Man</option>
                      <option value="Trans Woman">Trans Woman</option>
                      <option value="Non-binary">Non-binary</option>
                      <option value="Genderqueer">Genderqueer</option>
                      <option value="Genderfluid">Genderfluid</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Pronouns (Register only) */}
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Pronouns
                  </label>
                  <select
                    name="pronouns"
                    value={formData.pronouns}
                    onChange={handleChange}
                    className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                    data-testid="pronouns-select"
                  >
                    <option value="">Select pronouns</option>
                    <option value="he/him">he/him</option>
                    <option value="she/her">she/her</option>
                    <option value="they/them">they/them</option>
                    <option value="he/they">he/they</option>
                    <option value="she/they">she/they</option>
                    <option value="ze/zir">ze/zir</option>
                    <option value="xe/xem">xe/xem</option>
                    <option value="any pronouns">any pronouns</option>
                    <option value="ask me">ask me</option>
                  </select>
                </div>
              )}
              
              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-blue-500 text-white rounded-full font-semibold text-lg hover:bg-blue-600 transition-all btn-primary-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </span>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </button>
              
              {/* Terms */}
              {!isLogin && (
                <p className="text-xs text-slate-500 text-center">
                  By creating an account, you confirm you are 18+ and agree to our{' '}
                  <a href="#" className="text-blue-500 hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-blue-500 hover:underline">Privacy Policy</a>
                </p>
              )}
            </motion.form>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
